import React, { createContext, useContext, useEffect, useState } from 'react'
import { BrandRowFieldsFragment, ListFieldsFragment } from 'types/graphql'
import { useLists } from 'modules/member/lists/queries/useLists'
import { useBrandsDiscovery } from '../queries/getDiscoveryBrands'

interface Context {
  brands: BrandRowFieldsFragment[]
  lists: ListFieldsFragment[]
  listTitle: string | null
  loading?: boolean
  setFilter: (listId: string | null) => void
  filter: string | null
}

export const BrandCarouselContext = createContext<Context>({
  brands: [],
  lists: [],
  filter: null,
  listTitle: null,
  setFilter: () => {}
})

interface Props {
  children: React.ReactNode
  listIds: string[]
  onFilter?: () => void
}

export const BrandCarouselProvider = ({ children, listIds, onFilter }: Props) => {
  const [filter, setFilter] = useState<string | null>(null)

  const { data: discoveryData, loading: brandsLoading } = useBrandsDiscovery({ variables: { includePromotions: true } })
  const { data: listsData, loading: listsLoading } = useLists({
    variables: {
      listIds
    }
  })
  const [listTitle, setListTitle] = useState<string | null>(listsData?.lists.find((list) => list.id === filter)?.title ?? null)
  const [brands, setBrands] = useState<BrandRowFieldsFragment[]>(discoveryData?.brands.items ?? [])

  useEffect(() => {
    const brands = filter ? listsData?.lists.find((list) => list.id === filter)?.brands.items : discoveryData?.brands.items
    const listTitle = listsData?.lists.find((list) => list.id === filter)?.title ?? null
    setBrands(brands ?? [])
    setListTitle(listTitle)
    onFilter?.()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter])

  return (
    <BrandCarouselContext.Provider
      value={{ brands, loading: brandsLoading && listsLoading, listTitle, filter, setFilter, lists: listsData?.lists ?? [] }}>
      {children}
    </BrandCarouselContext.Provider>
  )
}

export const useBrandCarouselContext = () => useContext(BrandCarouselContext)
